import { useCallback } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';
import { normalizeSignData } from '#src/app/pages/petitionDetails/shared/utils';
import { identifyConstituentByScope } from '#src/app/shared/constituents';
import type { ConstituentIdentification } from '#src/app/shared/constituents';

import { useGdprConfig } from '../../useGdprConfig';

type Result = (formValues: FormValues) => ConstituentIdentification | null;

/**
 * Identifies the signer as a constituent of the petition from the submitted address. Runs once per sign
 * form submit: the result goes on `sign_intent` (CHANGE-100774) and is stored for the combo screen
 * (CHANGE-100766).
 *
 * Identifies from the normalized form values, the same input as the sign request: a Google Places
 * address only reaches the top-level city/state/country fields through `normalizeSignData`.
 *
 * Returns null for ineligible petitions. Owns its own try/catch: a failure is reported and returns null,
 * so it can never block the signature.
 */
export function useIdentifyConstituent(): Result {
	const { errorReporter } = useUtilityContext();
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const gdprConfig = useGdprConfig();

	return useCallback(
		(formValues) => {
			if (!petition.constituentExperimentEligible) return null;
			try {
				const { countryCode, stateCode, city } = normalizeSignData(formValues, gdprConfig);
				return identifyConstituentByScope(
					{
						countryCode: petition.country.countryCode,
						stateCode: petition.state?.stateCode,
						city: petition.city,
					},
					{ countryCode, stateCode, city },
				);
			} catch (error) {
				void errorReporter.report({
					error: error instanceof Error ? error : new Error('Constituent identification failed', { cause: error }),
					context: { feature: 'sign_form' },
					params: { petitionId: petition.id },
				});
				return null;
			}
		},
		[petition, gdprConfig, errorReporter],
	);
}
